import { Box, Button, CircularProgress, Paper, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Thunks } from '../../actions';
import { SierraDeviceActions } from '../../actions/actions/sierra-device-actions';
import deviceStyles from '../../app/device-management/DeviceStyle';
import { RESTRICT_SPEC_REGEX_FOR_NAME, DECIMAL_NUMBER, ONLY_NUMBER, FX30_ETHERNET, FX30S_SERIAL } from '../../constants';
import { useDeviceMaster } from '../../data/DataProviders/DeviceMasterProvider';
import { useSierraGatewayDeviceDetails } from '../../data/DataProviders/SierraGatewayDeviceDetailsProvider';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { isRecordFound, useSelectedOrganizationData } from '../../hooks/data-hooks';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { getDeviceInfoFromMasterData, IPAddressValidation, getChildDeviceLimit } from '../../lib/device-helpers';
import { Selectors } from '../../selectors';
import { SierraDeviceSelectors } from '../../selectors/device-selectors';
import { isDataLoaded } from '../../types';
import { SierraChildDeviceData } from '../../types/device';
import { useAuthHelper } from '../AuthContextProvider';
import { HeaderInfo } from '../HeaderInfo/HeaderInfo';
import { NavigationPromptModal } from '../NavigationPromptModal/NavigationPromptModal';

export const EditChildDevice: React.FC<{
    sierraChildDeviceDetails: SierraChildDeviceData | null;
    updatedChildData: Function;
}> = (props) => {
    const classes = deviceStyles();
    const { t } = useLanguageLocale();
    const dispatch = useDispatch();
    const authHelper = useAuthHelper();
    const { addToast } = useToasts();
    const history = useHistory();
    const currentAppRoute = useCurrentAppRoute();
    const deviceMasterData = useDeviceMaster();
    const gatewayDeviceDetails = useSierraGatewayDeviceDetails();
    const selectedOrganization = useSelectedOrganizationData();
    const childDeviceState = useSelector(Selectors.SierraDevice.deviceRegistration);
   const exerthermUnitIdValidation = useSelector(SierraDeviceSelectors.exerthermUnitIdValidation);
    const [isDirty, setIsDirty] = useState(false);
   const [unmodifiedChildDeviceInfo, setUnmodifiedChildDeviceInfo] = useState<SierraChildDeviceData | null>(null);
    const [modifiedChildDeviceInfo, setModifiedChildDeviceInfo] = useState<SierraChildDeviceData | null>(null);
    const [ipAddressError, setIPAddressError] = React.useState(false);
    const [deviceTypeList, setDeviceTypeList] = useState<any[]>([]);
    const orgName = isRecordFound(selectedOrganization) ? selectedOrganization.value.name : '';

    useEffect(() => {
        if (props.sierraChildDeviceDetails) {
            setUnmodifiedChildDeviceInfo(props.sierraChildDeviceDetails);
            setModifiedChildDeviceInfo(props.sierraChildDeviceDetails);
          dispatch(SierraDeviceActions.exerthermUnitIdValidation(false));
        }
    }, [props.sierraChildDeviceDetails, dispatch]);

    useEffect(() => {
        if (isDataLoaded(gatewayDeviceDetails)) {
            deviceMasterData.getDevicesInfo(orgName, gatewayDeviceDetails.value.childDeviceTypeList);
        }
    }, [gatewayDeviceDetails]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (
            isDataLoaded(deviceMasterData.deviceInfo) &&
            deviceMasterData.deviceInfo.value &&
            deviceMasterData.deviceInfo.value.length > 0 &&
            isDataLoaded(gatewayDeviceDetails) &&
            deviceMasterData.rowKey === gatewayDeviceDetails.value.childDeviceTypeList
        ) {
            const childDeviceInfo = getDeviceInfoFromMasterData(deviceMasterData.deviceInfo.value);
            if (childDeviceInfo) {
                setDeviceTypeList(childDeviceInfo.childDeviceTypes);
            }
        }
    }, [deviceMasterData.deviceInfo, gatewayDeviceDetails]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const isDataModified = JSON.stringify(unmodifiedChildDeviceInfo) !== JSON.stringify(modifiedChildDeviceInfo);
        setIsDirty(isDataModified);
    }, [unmodifiedChildDeviceInfo, modifiedChildDeviceInfo]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (exerthermUnitIdValidation) {
            addToast(
                'You have already configured maximum Device to use this unit id for the Connected Device. Please update this field to an unused unit id.',
                'error',
                'Maximum unit id limit exhausted'
            );
            dispatch(SierraDeviceActions.exerthermUnitIdValidation(false));
        }
    }, [ exerthermUnitIdValidation]); //eslint-disable-line react-hooks/exhaustive-deps

    const navigateToDeviceProfile = (): void => {
        if (currentAppRoute.type === 'EditDeviceProfile') {
            pushAppRoute(history, {
                type: 'ChildDeviceProfile',
                deviceId: currentAppRoute.deviceId,
                locationId: currentAppRoute.locationId,
                orgId: currentAppRoute.orgId,
                siteId: currentAppRoute.siteId,
                childDeviceId: currentAppRoute.childDeviceId,
            });
        }
    };

    useEffect(() => {
        if (!childDeviceState.loading && childDeviceState.success) {
            navigateToDeviceProfile();
            addToast('Device updated successfully.');
            props.updatedChildData(modifiedChildDeviceInfo);
        }
    }, [childDeviceState.loading, childDeviceState.success]); //eslint-disable-line react-hooks/exhaustive-deps
    const onChangeDeviceName = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (RESTRICT_SPEC_REGEX_FOR_NAME.test(e.target.value) && modifiedChildDeviceInfo) {
            const deviceNameObj: SierraChildDeviceData = { ...modifiedChildDeviceInfo };
            deviceNameObj.name = e.target.value;
            setModifiedChildDeviceInfo(deviceNameObj);
        }
    };
    const onChangeIpAddress = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        setIPAddressError(false);
        if ((e.target.value === '' || DECIMAL_NUMBER.test(e.target.value)) && modifiedChildDeviceInfo) {
            const deviceIpAddressObj: SierraChildDeviceData = { ...modifiedChildDeviceInfo };
            deviceIpAddressObj.ipAddress = e.target.value;
            setModifiedChildDeviceInfo(deviceIpAddressObj);
        }
    };

    const onChangeIpPort = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if ((e.target.value === '' || ONLY_NUMBER.test(e.target.value)) && modifiedChildDeviceInfo) {
            const deviceIpPortObj: SierraChildDeviceData = { ...modifiedChildDeviceInfo };
            deviceIpPortObj.ipPort = Number(e.target.value);
            setModifiedChildDeviceInfo(deviceIpPortObj);
        }
    };
    const onChangeUnitId = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (
            (e.target.value === '' && modifiedChildDeviceInfo) ||
            (ONLY_NUMBER.test(e.target.value) &&
                modifiedChildDeviceInfo &&
                modifiedChildDeviceInfo.parentModelType &&
                ((modifiedChildDeviceInfo.parentModelType.toLowerCase() === FX30S_SERIAL &&
                    Number(e.target.value) <= 247) ||
                    (modifiedChildDeviceInfo.parentModelType.toLowerCase() === FX30_ETHERNET &&
                        (Number(e.target.value) <= 247 || Number(e.target.value) === 255))))
        ) {
            const deviceUnitIdObj: SierraChildDeviceData = { ...modifiedChildDeviceInfo };
            deviceUnitIdObj.unitId = isNaN(parseInt(e.target.value)) ? 0 : parseInt(e.target.value);
            setModifiedChildDeviceInfo(deviceUnitIdObj);
        }
    };

    const isDisableEthernet = (): boolean => {
        if (modifiedChildDeviceInfo) {
            return (
                !isDirty ||
                !modifiedChildDeviceInfo.name.trim() ||
                modifiedChildDeviceInfo.unitId === 0 ||
                !modifiedChildDeviceInfo.ipAddress.trim()
            );
        }
        return true;
    };
    const isDisableSerial = (): boolean => {
        if (modifiedChildDeviceInfo)
            return !isDirty || !modifiedChildDeviceInfo.name.trim() || modifiedChildDeviceInfo.unitId === 0;
        return true;
    };

    const isDisable = (): boolean => {
        if (!childDeviceState.loading && props.sierraChildDeviceDetails) {
            return props.sierraChildDeviceDetails.parentModelType &&
                props.sierraChildDeviceDetails.parentModelType.toLowerCase() === FX30_ETHERNET
                ? isDisableEthernet()
                : props.sierraChildDeviceDetails.parentModelType &&
                  props.sierraChildDeviceDetails.parentModelType.toLowerCase() === FX30S_SERIAL
                ? isDisableSerial()
                : true;
        }
        return true;
    };

    const onSubmit = (): void => {
        let allowDuplicateUnitId = 0;
        setIsDirty(false);
        if (modifiedChildDeviceInfo && unmodifiedChildDeviceInfo) {
            const value = IPAddressValidation(
                unmodifiedChildDeviceInfo.parentModelType,
                modifiedChildDeviceInfo.ipAddress
            );
            setIPAddressError(!value);
            if (value) {
                const selectedObj = deviceTypeList.find((x) => x.value.modelType === unmodifiedChildDeviceInfo.model);
                const childDeviceLimit = getChildDeviceLimit(selectedObj);
                if (childDeviceLimit && childDeviceLimit.allowedUnitIdDulipcate > 0) {
                    allowDuplicateUnitId = childDeviceLimit.allowedUnitIdDulipcate;
                }
                if (modifiedChildDeviceInfo.unitId === unmodifiedChildDeviceInfo.unitId) {
                    dispatch(
                        Thunks.SierraDevice.UpdateChildDevice(
                            modifiedChildDeviceInfo.pxWhiteDeviceId || '',
                            modifiedChildDeviceInfo.siteId,
                            modifiedChildDeviceInfo.name,
                            modifiedChildDeviceInfo.parentPxWhiteGatewayId,
                            modifiedChildDeviceInfo.ipAddress,
                            modifiedChildDeviceInfo.unitId,
                            modifiedChildDeviceInfo.ipPort || 0,
                            authHelper,
                            addToast
                        )
                    );
                } else {
                    dispatch(
                        Thunks.SierraDevice.UpdateChildDeviceWithUnitIdValidation(
                            modifiedChildDeviceInfo.pxWhiteDeviceId || '',
                            modifiedChildDeviceInfo.siteId,
                            modifiedChildDeviceInfo.name,
                            modifiedChildDeviceInfo.parentPxWhiteGatewayId,
                            modifiedChildDeviceInfo.ipAddress,
                            modifiedChildDeviceInfo.unitId,
                            modifiedChildDeviceInfo.model,
                            modifiedChildDeviceInfo.ipPort || 0,
                            authHelper,
                            addToast,
                            allowDuplicateUnitId
                        )
                    );
                }
            }
        }
    };

    return (
        <>
            <Paper style={{ padding: 48 }}>
                <HeaderInfo title="Edit Device Information" entity="when editing a Device" />
                {modifiedChildDeviceInfo && (
                    <Box display="flex" flexDirection="column" alignItems="left">
                        <Box className={classes.paddingTextbox}>
                            <TextField
                                id="child-device-name-text"
                                inputProps={{
                                    'data-testid': 'child-device-name-text',
                                    maxLength: 100,
                                }}
                                variant="filled"
                                onChange={(e): void => {
                                    onChangeDeviceName(e);
                                }}
                                fullWidth
                                required
                                label="Device Name"
                                value={modifiedChildDeviceInfo.name}
                                helperText={
                                    <Box component="span" display="flex" justifyContent="flex-end">
                                        {modifiedChildDeviceInfo.name.length}/100
                                    </Box>
                                }
                            />
                        </Box>
                        {modifiedChildDeviceInfo.parentModelType &&
                            modifiedChildDeviceInfo.parentModelType.toLowerCase() === FX30_ETHERNET && (
                                <Box
                                    style={{ paddingTop: 8 }}
                                    className={`${classes.padding8} ${classes.paddingTextbox} `}
                                >
                                    <TextField
                                        inputProps={{
                                            'data-testid': 'child-rtu-address-text',
                                            maxLength: 15,
                                        }}
                                        variant="filled"
                                        fullWidth
                                        required
                                        error={ipAddressError}
                                        helperText={ipAddressError ? 'Invalid IP Address' : ''}
                                        id="child-rtu-address-text"
                                        label="Modbus (TCP/IP) Address"
                                        value={modifiedChildDeviceInfo.ipAddress}
                                        onChange={(e): void => {
                                            onChangeIpAddress(e);
                                        }}
                                    />
                                </Box>
                            )}
                        <Box className={classes.paddingTextbox}>
                            <TextField
                                id="rtu-server-address-text"
                                inputProps={{
                                    'data-testid': 'rtu-server-address-text',
                                    maxLength: 3,
                                }}
                                variant="filled"
                                fullWidth
                                required
                                helperText={
                                    modifiedChildDeviceInfo.parentModelType &&
                                    modifiedChildDeviceInfo.parentModelType.toLowerCase() === FX30_ETHERNET
                                        ? 'Valid client Unit ID are 1-247 and 255.'
                                        : 'Valid client Modbus RTU Addresses are 1-247 .'
                                }
                                label={
                                    modifiedChildDeviceInfo.parentModelType &&
                                    modifiedChildDeviceInfo.parentModelType.toLowerCase() === FX30_ETHERNET
                                        ? 'Unit ID'
                                        : 'Modbus (RTU) Address'
                                }
                                value={modifiedChildDeviceInfo.unitId === 0 ? '' : modifiedChildDeviceInfo.unitId}
                                onChange={(e): void => {
                                    onChangeUnitId(e);
                                }}
                            />
                        </Box>
                        {modifiedChildDeviceInfo.parentModelType &&
                            modifiedChildDeviceInfo.parentModelType.toLowerCase() === FX30_ETHERNET && (
                                <Box
                                    style={{ paddingTop: 8 }}
                                    className={`${classes.padding8} ${classes.paddingTextbox} `}
                                >
                                    <TextField
                                        inputProps={{
                                            'data-testid': 'child-ipPort',
                                            maxLength: 5,
                                        }}
                                        variant="filled"
                                        fullWidth
                                        required
                                        id="child-ipPort"
                                        label="IP Port"
                                        value={modifiedChildDeviceInfo.ipPort}
                                        onChange={(e): void => {
                                            onChangeIpPort(e);
                                        }}
                                    />
                                </Box>
                            )}
                        <Box className={classes.iconRightEnd}>
                            <Button
                                style={{ marginRight: 16 }}
                                variant="outlined"
                                color="primary"
                                data-testid="cancel-device"
                                onClick={navigateToDeviceProfile}
                            >
                                {t('ACTIONS.CANCEL')}
                            </Button>
                            <Button
                                startIcon={
                                    childDeviceState.loading ? (
                                        <CircularProgress size="1rem" style={{ color: 'white' }} />
                                    ) : (
                                        <></>
                                    )
                                }
                                variant="contained"
                                disabled={isDisable()}
                                color="primary"
                                data-testid="edit-device-button"
                                onClick={onSubmit}
                            >
                                {t('ACTIONS.SUBMITCHANGES')}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>
            <NavigationPromptModal isDirty={isDirty} />
        </>
    );
};
